// Custom React Hook for API calls
// Provides loading states, error handling, and data management
import { useState, useEffect, useCallback } from 'react';

export const useApi = (apiFunction, dependencies = [], autoExecute = true) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (...args) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiFunction(...args);
      setData(result);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, dependencies);

  // Auto-execute when dependencies change and autoExecute is true
  useEffect(() => {
    if (autoExecute) {
      // No deps: run once on mount. Explicit false (e.g. enabled=false): skip. Otherwise run when required params present.
      const hasDisabled = dependencies.some(dep => dep === false);
      const hasRequiredParams = !hasDisabled && (dependencies.length === 0 || dependencies.some(dep => dep !== null && dep !== undefined && dep !== ''));
      if (hasRequiredParams) {
        execute();
      }
    }
    // execute is memoized with dependencies, so changes to dependencies will trigger execute to update
    // which will then trigger this effect to re-run
  }, [autoExecute, execute]);

  const refetch = useCallback(() => {
    return execute();
  }, [execute]);

  return { data, loading, error, execute, refetch };
};

export const useApiState = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const setLoadingState = useCallback((isLoading) => {
    setLoading(isLoading);
    if (isLoading) {
      setError(null);
    }
  }, []);

  const setDataState = useCallback((newData) => {
    setData(newData);
    setError(null);
  }, []);

  const setErrorState = useCallback((newError) => {
    setError(newError);
  }, []);

  const reset = useCallback(() => {
    setData(null);
    setLoading(false);
    setError(null);
  }, []);

  return {
    data,
    loading,
    error,
    setLoadingState,
    setDataState,
    setErrorState,
    reset
  };
};

export const useApiWithCache = (apiFunction, cacheKey, dependencies = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  const execute = useCallback(async (...args) => {
    const now = Date.now();
    const cacheExpiry = 5 * 60 * 1000; // 5 minutes

    // Check if we have cached data and it's still fresh
    if (data && lastFetch && (now - lastFetch) < cacheExpiry) {
      return data;
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await apiFunction(...args);
      setData(result);
      setLastFetch(now);
      
      // Store in localStorage for persistence
      if (cacheKey) {
        localStorage.setItem(cacheKey, JSON.stringify({
          data: result,
          timestamp: now
        }));
      }
      
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, dependencies);

  // Load from cache on mount
  useEffect(() => {
    if (cacheKey) {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          const { data: cachedData, timestamp } = JSON.parse(cached);
          const now = Date.now();
          const cacheExpiry = 5 * 60 * 1000; // 5 minutes

          if ((now - timestamp) < cacheExpiry) {
            setData(cachedData);
            setLastFetch(timestamp);
          }
        } catch (err) {
          // Invalid cache data, ignore
        }
      }
    }
  }, [cacheKey]);

  return { data, loading, error, execute };
};

export const usePagination = (apiFunction, externalParams = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  // Use external params directly instead of internal state
  const params = externalParams;

  const fetchData = useCallback(async (page = 1, newParams = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiFunction({
        page,
        limit: pagination.limit,
        ...params,
        ...newParams
      });
      
      if (page === 1) {
        setData(result.data || []);
      } else {
        setData(prev => [...prev, ...(result.data || [])]);
      }
      
      setPagination(result.pagination || pagination);
      // No need to set params since we use external params
      
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiFunction, externalParams, pagination.limit]);

  // Auto-fetch data on mount and when external params change
  useEffect(() => {
    const executeFetch = async () => {
      try {
        await fetchData(1);
      } catch (err) {
        console.error('❌ Auto-fetch error:', err);
      }
    };
    executeFetch();
  }, [JSON.stringify(externalParams)]); // Respond to external params changes

  const loadMore = useCallback(() => {
    if (pagination.page < pagination.pages && !loading) {
      fetchData(pagination.page + 1);
    }
  }, [fetchData, pagination.page, pagination.pages, loading]);

  const refresh = useCallback(() => {
    fetchData(1);
  }, [fetchData]);

  const search = useCallback((searchParams) => {
    fetchData(1, searchParams);
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    pagination,
    fetchData,
    loadMore,
    refresh,
    search
  };
};

export const useInfiniteScroll = (apiFunction, initialParams = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [params, setParams] = useState(initialParams);

  const fetchData = useCallback(async (pageNum = 1, newParams = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiFunction({
        page: pageNum,
        limit: 20,
        ...params,
        ...newParams
      });
      
      const newData = result.data || [];
      
      if (pageNum === 1) {
        setData(newData);
      } else {
        setData(prev => [...prev, ...newData]);
      }
      
      setHasMore(newData.length > 0);
      setPage(pageNum);
      setParams({ ...params, ...newParams });
      
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiFunction, params]);

  const loadMore = useCallback(() => {
    if (hasMore && !loading) {
      fetchData(page + 1);
    }
  }, [fetchData, page, hasMore, loading]);

  const refresh = useCallback(() => {
    setPage(1);
    setHasMore(true);
    fetchData(1);
  }, [fetchData]);

  const search = useCallback((searchParams) => {
    setPage(1);
    setHasMore(true);
    fetchData(1, searchParams);
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
    search
  };
};

export const useApiMutation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const mutate = useCallback(async (apiFunction, ...args) => {
    setLoading(true);
    setError(null);
    setData(null);
    
    try {
      const result = await apiFunction(...args);
      setData(result);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setData(null);
  }, []);

  return {
    mutate,
    loading,
    error,
    data,
    reset
  };
};
